import React, { useState } from "react";
import { Password } from "primereact/password";
import { Button } from "@mui/material";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Swal from "sweetalert2";

const ChangePasswordTab = () => {
  // States for change password form
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});

  // States for forgot password popup
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotError, setForgotError] = useState("");

  // Password requirement checks for new password
  const minLength12 = newPassword.length >= 12;
  const hasUppercase = /[A-Z]/.test(newPassword);
  const hasLowercase = /[a-z]/.test(newPassword);
  const hasNumber = /\d/.test(newPassword);
  const hasSpecial = /[^A-Za-z0-9]/.test(newPassword);

  const handleChangePassword = () => {
    const newErrors = {};

    if (!oldPassword.trim()) {
      newErrors.oldPassword = "Old password is required";
    }
    if (!newPassword.trim()) {
      newErrors.newPassword = "New password is required";
    } else {
      if (!minLength12) newErrors.newPassword = "Password must be at least 12 characters long.";
      if (!hasUppercase) newErrors.newPassword = "Password must contain at least one uppercase letter.";
      if (!hasLowercase) newErrors.newPassword = "Password must contain at least one lowercase letter.";
      if (!hasNumber) newErrors.newPassword = "Password must contain at least one digit.";
      if (!hasSpecial) newErrors.newPassword = "Password must contain at least one special character.";
    }
    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your new password.";
    } else if (confirmPassword !== newPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      // Clear errors and show success popup
      setErrors({});
      Swal.fire({
        icon: "success",
        title: '<h4 style="color:#407775;font-size: 20px;">Success!</h4>',
        text: "Password changed successfully!",
        iconHtml: '<img src="assets/img/successicon.png" style="width: 55px; height: 55px;" />',
      });
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  // Forgot password modal handler with proper email validation
  const handleForgotSubmit = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!forgotEmail.trim()) {
      setForgotError("Please enter your email address.");
      return;
    }
    if (!emailRegex.test(forgotEmail.trim())) {
      setForgotError("Please enter a valid email address.");
      return;
    }
    setForgotError("");
    console.log("Sending password reset email to:", forgotEmail);
    setShowForgotModal(false);
    Swal.fire({
      icon: "success",
      title: '<h4 style="color:#407775;font-size: 20px;">Reset Email Sent</h4>',
      text: `We have sent a password reset link to ${forgotEmail}.`,
      iconHtml: '<img src="assets/img/successicon.png" style="width: 55px; height: 55px;" />',
      confirmButtonText: "Got it",
      cancelButtonText: "Send again",
      showCancelButton: true,
      customClass: {
        cancelButton: "swal2-cancel-outlined", // Use your CSS to outline this button
        confirmButton: "swal2-confirm-filled",
      },
    }).then((result) => {
      if (result.dismiss === Swal.DismissReason.cancel) {
        console.log("Resending reset email to:", forgotEmail);
        Swal.fire({
          icon: "success",
          title: '<h4 style="color:#407775;font-size: 20px;">Reset Email Sent</h4>',
          text: `A new password reset email has been sent to ${forgotEmail}.`,
          iconHtml: '<img src="assets/img/successicon.png" style="width: 55px; height: 55px;" />',
        });
      }
    });
  };

  // Clear field errors on input change
  const handleOldPasswordChange = (e) => {
    setOldPassword(e.target.value);
    if (e.target.value.trim()) {
      setErrors((prev) => ({ ...prev, oldPassword: "" }));
    }
  };

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
    if (e.target.value.trim()) {
      setErrors((prev) => ({ ...prev, newPassword: "" }));
    }
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    if (e.target.value.trim()) {
      setErrors((prev) => ({ ...prev, confirmPassword: "" }));
    }
  };

  return (
    <div className="change-password-card profile-tabs">
      {/* Change Password Form */}
      <div className="form-group">
        <label htmlFor="oldPassword" className="formlable d-block">
          Old Password
        </label>
        <Password id="oldPassword" value={oldPassword} onChange={handleOldPasswordChange} toggleMask feedback={false} className={errors.oldPassword ? "p-invalid" : ""} placeholder="Enter old password" />
        {errors.oldPassword && <small className="p-error">{errors.oldPassword}</small>}
      </div>

      <div className="form-group">
        <label htmlFor="newPassword" className="formlable d-block">
          New Password
        </label>
        <Password id="newPassword" value={newPassword} onChange={handleNewPasswordChange} toggleMask promptLabel="Enter new password" weakLabel="Weak" mediumLabel="Medium" strongLabel="Strong" className={errors.newPassword ? "p-invalid" : ""} placeholder="Enter new password" />
        {errors.newPassword && <small className="p-error">{errors.newPassword}</small>}

        <ul style={{ listStyle: "none", padding: 0, marginTop: "1rem" }}>
          <li style={{ color: newPassword ? (minLength12 ? "green" : "red") : "#A2A2A2" }}>• Minimum character 12</li>
          <li style={{ color: newPassword ? (hasUppercase ? "green" : "red") : "#A2A2A2" }}>• One uppercase character</li>
          <li style={{ color: newPassword ? (hasLowercase ? "green" : "red") : "#A2A2A2" }}>• One lowercase character</li>
          <li style={{ color: newPassword ? (hasSpecial ? "green" : "red") : "#A2A2A2" }}>• One special character</li>
          <li style={{ color: newPassword ? (hasNumber ? "green" : "red") : "#A2A2A2" }}>• One numeric character</li>
        </ul>
      </div>

      <div className="form-group">
        <label htmlFor="confirmPassword" className="formlable d-block">
          Confirm New Password
        </label>
        <Password id="confirmPassword" value={confirmPassword} onChange={handleConfirmPasswordChange} toggleMask feedback={false} className={errors.confirmPassword ? "p-invalid" : ""} placeholder="Enter new password again" />
        {errors.confirmPassword && <small className="p-error">{errors.confirmPassword}</small>}
      </div>

      <Button variant="contained" className="profile-btn mt-3" onClick={handleChangePassword}>
        Change Password
      </Button>

      {/* Forgot Password Link */}
      <div className="mt-3">
        <Link
          to="#!"
          style={{ textDecoration: "underline", color: "#407775", cursor: "pointer" }}
          onClick={() => {
            setForgotEmail(""); // Clear the email field each time the popup opens
            setShowForgotModal(true);
            setForgotError("");
          }}
        >
          Forgot Password?
        </Link>
      </div>

      {/* Forgot Password Modal */}
      <Modal show={showForgotModal} onHide={() => setShowForgotModal(false)} centered dialogClassName="custom-modal-width business-modal">
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body style={{ textAlign: "center" }}>
          <div className="steps__containt-hader edit-form">
            <h3 className="title mb-2">Forgot Password</h3>
            <p className="mx-5">Enter your email address and we’ll send you a link to reset your password.</p>
            <input
              type="email"
              placeholder="email@example.com"
              className="form-control"
              value={forgotEmail}
              onChange={(e) => {
                setForgotEmail(e.target.value);
                if (e.target.value.trim()) {
                  setForgotError("");
                }
              }}
            />
            {forgotError && <small className="p-error">{forgotError}</small>}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex flex-column justify-content-between gap-3 w-100 text-center" style={{ margin: "20px 50px 0" }}>
            <Button onClick={handleForgotSubmit} className="next-button" style={{ color: "#fff!important", width: "100%" }}>
              Reset your password
            </Button>
            <Link to="#!" style={{ textDecoration: "underline", color: "#407775", cursor: "pointer", fontSize: "13px" }} onClick={() => setShowForgotModal(false)}>
              Back to Log In
            </Link>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ChangePasswordTab;
